import React, { useState, Suspense, lazy } from 'react';
import PageHeader from '../common/PageHeader';
import Card from '../common/Card';
import { LoadingState } from '../common/Spinner';

const BusinessAnalytics = lazy(() => import('../reports/BusinessAnalytics'));
const StockReports = lazy(() => import('../reports/StockReports'));
const LiterStatementReports = lazy(() => import('../reports/LiterStatementReports'));
const MonthlyReports = lazy(() => import('../reports/MonthlyReports'));

const REPORT_TYPES = [
  {
    id: 'business-analytics',
    icon: 'fas fa-chart-bar',
    title: 'Business Analytics',
    description: 'Comprehensive business performance reports',
    color: 'from-blue-500 to-cyan-500'
  },
  {
    id: 'stock',
    icon: 'fas fa-boxes',
    title: 'Stock Reports',
    description: 'Inventory and stock analysis',
    color: 'from-purple-500 to-pink-500'
  },
  {
    id: 'liter',
    icon: 'fas fa-wine-bottle',
    title: 'Liter Statement Reports',
    description: 'Liter sales, purchases, and category trends',
    color: 'from-green-500 to-emerald-500'
  },
  {
    id: 'monthly',
    icon: 'fas fa-calendar-alt',
    title: 'Monthly Reports',
    description: 'Monthly summaries and trends',
    color: 'from-orange-500 to-red-500'
  }
];

const ReportsPage = () => {
  // Mirrors the internal view-switching pattern used by other multi-step
  // pages in the app (e.g. StockCheckPage's `view` state) rather than
  // introducing react-router routes, since Dashboard.js already owns page
  // navigation via its own activePage state, not the URL.
  const [activeReport, setActiveReport] = useState(null);

  const handleBack = () => setActiveReport(null);

  const renderReport = () => {
    if (activeReport === 'business-analytics') return <BusinessAnalytics onBack={handleBack} />;
    if (activeReport === 'stock') return <StockReports onBack={handleBack} />;
    if (activeReport === 'liter') return <LiterStatementReports onBack={handleBack} />;
    if (activeReport === 'monthly') return <MonthlyReports onBack={handleBack} />;
    return null;
  };

  if (activeReport) {
    return (
      <Suspense fallback={<LoadingState label="Loading report module..." />}>
        {renderReport()}
      </Suspense>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-3">
      <PageHeader
        icon="fa-chart-line"
        title="Reports & Analytics"
        subtitle="Generate and view comprehensive business reports"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 md:mb-8">
        {REPORT_TYPES.map((report) => (
          <Card
            key={report.id}
            padding="lg"
            hover
            className="relative overflow-hidden text-left w-full cursor-pointer group"
            role="button"
            tabIndex={0}
            onClick={() => setActiveReport(report.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setActiveReport(report.id);
              }
            }}
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br opacity-10 rounded-full blur-2xl -mr-16 -mt-16" aria-hidden="true"></div>
            <div className="relative z-10">
              <div className={`w-16 h-16 bg-gradient-to-br ${report.color} rounded-2xl flex items-center justify-center mb-6 shadow-lg`}>
                <i className={`${report.icon} text-white text-2xl`} aria-hidden="true"></i>
              </div>
              <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">{report.title}</h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">{report.description}</p>
              <span className="inline-flex items-center gap-2 text-sm font-bold text-primary dark:text-accent group-hover:gap-3 transition-all">
                View Report <i className="fas fa-arrow-right text-xs" aria-hidden="true"></i>
              </span>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ReportsPage;
